<template>
  <!-- <f7-page :nav-title="$t('negotiation.title')"> -->
    <f7-block-title large >{{ $t('negotiation.title') }}</f7-block-title>
    <f7-block>
      <f7-block-title>{{ $t('negotiation.manageOffers') }}</f7-block-title>
      <p class="neg-mgr-subtitle">
        {{ $t('negotiation.manageSubtitle') }}
      </p>
    </f7-block>

    <f7-segmented strong round class="neg-mgr-tabs">
      <f7-button round raised
        :active="activeTab === 'PROPOSED'"
        @click="activeTab = 'PROPOSED'"
      >
        {{ $t('negotiation.pending') }}
        <span v-if="proposedCount" class="neg-tab-badge">{{ proposedCount }}</span>
      </f7-button>
      <f7-button round raised
        :active="activeTab === 'ACCEPTED'"
        @click="activeTab = 'ACCEPTED'"
      >
        {{ $t('negotiation.accepted') }}
      </f7-button>
      <f7-button round raised
        :active="activeTab === 'REJECTED'"
        @click="activeTab = 'REJECTED'"
      >
        {{ $t('negotiation.rejected') }}
      </f7-button>
      <f7-button round raised
        :active="activeTab === 'COUNTERED'"
        @click="activeTab = 'COUNTERED'"
      >
        {{ $t('negotiation.countered') }}
      </f7-button>
    </f7-segmented>

    <template v-if="activeTab === 'PROPOSED'">
      <div v-if="!proposed.length" class="neg-mgr-empty">
        <f7-icon f7="hand_thumbsup_fill" size="32" color="#c0c0c0" />
        <p>{{ $t('negotiation.noPending') }}</p>
      </div>
      <f7-card
        v-for="n in proposed"
        :key="n.id"
        class="neg-mgr-card"
      >
        <f7-card-content>
          <div class="neg-mgr-card-header">
            <div class="neg-mgr-buyer">
              <div class="neg-mgr-buyer-avatar">{{ (n.buyerName || 'A')[0] }}</div>
              <div>
                <div class="neg-mgr-buyer-name">{{ n.buyerName }}</div>
                <div class="neg-mgr-product">{{ n.productName }}</div>
              </div>
            </div>
            <div class="neg-mgr-offer-price">
              {{ formatPrice(n.offeredPrice,n.currency) }}
            </div>
          </div>
          <p v-if="n.buyerMessage" class="neg-mgr-message">"{{ n.buyerMessage }}"</p>
          <div class="neg-mgr-card-meta">
            <span class="neg-mgr-date">{{ formatDate(n.createdAt) }}</span>
            <span class="neg-mgr-expires">{{ $t('negotiation.expires') }} {{ formatExpiry(n.expiresAt) }}</span>
          </div>
          <div class="neg-mgr-actions">
            <f7-button fill small round raised color="green" @click="onAccept(n)">
              {{ $t('negotiation.accept') }}
            </f7-button>
            <f7-button outline raised round small color="blue" @click="openCounterOffer(n)">
              {{ $t('negotiation.counterOffer') }}
            </f7-button>
            <f7-button outline raised round small color="red" @click="onReject(n)">
              {{ $t('negotiation.reject') }}
            </f7-button>
          </div>
        </f7-card-content>
      </f7-card>
    </template>

    <template v-if="activeTab === 'ACCEPTED'">
      <div v-if="!accepted.length" class="neg-mgr-empty">
        <f7-icon f7="checkmark_circle" size="32" color="#c0c0c0" />
        <p>{{ $t('negotiation.noAccepted') }}</p>
      </div>
      <f7-card
        v-for="n in accepted"
        :key="n.id"
        class="neg-mgr-card neg-mgr-card--accepted"
      >
        <f7-card-content>
          <div class="neg-mgr-card-header">
            <div class="neg-mgr-buyer">
              <div class="neg-mgr-buyer-avatar neg-mgr-buyer-avatar--accepted">{{ (n.buyerName || 'A')[0] }}</div>
              <div>
                <div class="neg-mgr-buyer-name">{{ n.buyerName }}</div>
                <div class="neg-mgr-product">{{ n.productName }}</div>
              </div>
            </div>
            <div class="neg-mgr-offer-price neg-mgr-offer-price--accepted">
              {{ formatPrice(n.offeredPrice,n.currency) }}
            </div>
          </div>
          <div class="neg-mgr-accepted-meta">
            <span>{{ $t('negotiation.acceptedOn') }} {{ formatDate(n.acceptedAt, n.currency) }}</span>
          </div>
        </f7-card-content>
      </f7-card>
    </template>

    <template v-if="activeTab === 'REJECTED'">
      <div v-if="!rejected.length" class="neg-mgr-empty">
        <f7-icon f7="xmark_circle" size="32" color="#c0c0c0" />
        <p>{{ $t('negotiation.noRejected') }}</p>
      </div>
      <f7-card
        v-for="n in rejected"
        :key="n.id"
        class="neg-mgr-card neg-mgr-card--rejected"
      >
        <f7-card-content>
          <div class="neg-mgr-card-header">
            <div class="neg-mgr-buyer">
              <div class="neg-mgr-buyer-avatar neg-mgr-buyer-avatar--rejected">{{ (n.buyerName || 'A')[0] }}</div>
              <div>
                <div class="neg-mgr-buyer-name">{{ n.buyerName }}</div>
                <div class="neg-mgr-product">{{ n.productName }}</div>
              </div>
            </div>
            <div class="neg-mgr-offer-price neg-mgr-offer-price--rejected">
              {{ formatPrice(n.offeredPrice, n.currency) }}
            </div>
          </div>
          <p v-if="n.sellerMessage" class="neg-mgr-message">"{{ n.sellerMessage }}"</p>
        </f7-card-content>
      </f7-card>
    </template>

    <template v-if="activeTab === 'COUNTERED'">
      <div v-if="!countered.length" class="neg-mgr-empty">
        <f7-icon f7="arrow_2_circlepath" size="32" color="#c0c0c0" />
        <p>{{ $t('negotiation.noCountered') }}</p>
      </div>
      <f7-card
        v-for="n in countered"
        :key="n.id"
        class="neg-mgr-card neg-mgr-card--countered"
      >
        <f7-card-content>
          <div class="neg-mgr-card-header">
            <div class="neg-mgr-buyer">
              <div class="neg-mgr-buyer-avatar neg-mgr-buyer-avatar--countered">{{ (n.buyerName || 'A')[0] }}</div>
              <div>
                <div class="neg-mgr-buyer-name">{{ n.buyerName }}</div>
                <div class="neg-mgr-product">{{ n.productName }}</div>
              </div>
            </div>
            <div class="neg-mgr-prices">
              <span class="neg-mgr-price-original">{{ formatPrice(n.offeredPrice,n.currency) }}</span>
              <f7-icon f7="arrow_right" size="12" color="#8e8e93" />
              <span class="neg-mgr-price-counter">{{ formatPrice(n.counteredPrice, n.currency) }}</span>
            </div>
          </div>
          <p v-if="n.sellerMessage" class="neg-mgr-message">"{{ n.sellerMessage }}"</p>
          <div class="neg-mgr-actions">
            <f7-button fill raised round small color="green" @click="onAccept(n)">
              {{ $t('negotiation.acceptCounterOffer') }}
            </f7-button>
            <f7-button outline raised round small color="red" @click="onReject(n)">
              {{ $t('negotiation.reject') }}
            </f7-button>
          </div>
        </f7-card-content>
      </f7-card>
    </template>

    <AdaptiveSheet
      v-model:opened="counterSheetOpen"
      swipe-to-close
      :tablet-fullscreen="false"
      @closed="counterTarget = null; counterPrice = ''; counterMessage = ''"
    >
      <f7-page-content>
         <div class="card-header">
    
          <f7-block-title class="neg-sheet-title">{{ $t('negotiation.counterOfferTitle') }}</f7-block-title>
          <f7-link sheet-close popup-close style="right:8px"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
        </div>
        <p v-if="counterTarget" class="neg-counter-hint">
          {{ $t('negotiation.offerFrom') }} <strong>{{ counterTarget.buyerName }}</strong> :
          {{ formatPrice(counterTarget.offeredPric, counterTarget.currency) }}
        </p>
        <f7-list inset strong>
          <f7-list-input
            :label="$t('negotiation.yourCounterOffer')"
            type="text"
            inputmode="numeric"
            :value="counterPrice"
            @input="counterPrice = $event.target.value.replace(/[^\d]/g, '')"
            :placeholder="$t('negotiation.enterPrice')"
          >
            <template #media><span class="neg-currency">{{counterTarget.currency}}</span></template>
          </f7-list-input>
          <f7-list-input
            :label="$t('negotiation.messageOptional')"
            type="textarea"
            :value="counterMessage"
            @input="counterMessage = $event.target.value"
            :placeholder="$t('negotiation.explainCounterOffer')"
            :rows="3"
          />
        </f7-list>
        <f7-block>
          <f7-button
            large fill raised round
            :disabled="!counterPrice || parseInt(counterPrice) <= 0"
            @click="submitCounter"
          >
            {{ $t('negotiation.sendCounterOffer') }}
          </f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  <!-- </f7-page> -->
</template>

<script setup>
import { ref, computed } from 'vue'
import { f7 } from 'framework7-vue'
import { useMarketplaceStore, NEGOTIATION_STATUS_META } from '../../js/pinia-stores/marketplace.js'
import AdaptiveSheet from '../common/AdaptiveSheet.vue'
import i18n from '../../js/i18n.js'

const props = defineProps({
  groupId: { type: String, default: '' },
})

const { t } = i18n.global
const store = useMarketplaceStore()

const activeTab = ref('PROPOSED')

const allNegotiations = computed(() => {
  if (props.groupId) {
    return store.negotiations.filter((n) => n.groupId === props.groupId)
  }
  return store.negotiations
})

const proposed = computed(() => allNegotiations.value.filter((n) => n.status === 'PROPOSED'))
const accepted = computed(() => allNegotiations.value.filter((n) => n.status === 'ACCEPTED'))
const countered = computed(() => allNegotiations.value.filter((n) => n.status === 'COUNTERED'))
const rejected = computed(() => allNegotiations.value.filter((n) => n.status === 'REJECTED'))
const proposedCount = computed(() => proposed.value.length)

const counterSheetOpen = ref(false)
const counterTarget = ref(null)
const counterPrice = ref('')
const counterMessage = ref('')

function formatPrice(amount,currency) {
  if (!amount) return `0 ${currency}`
  return `${Math.round(amount).toLocaleString('fr-FR')} ${currency}`
}

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function formatExpiry(iso) {
  if (!iso) return ''
  const diff = new Date(iso).getTime() - Date.now()
  if (diff <= 0) return t('negotiation.expired')
  const hours = Math.floor(diff / 3600000)
  if (hours < 24) return t('negotiation.inHours', { hours })
  const days = Math.floor(hours / 24)
  return t('negotiation.inDays', { days })
}

function onAccept(n) {
  f7.dialog.confirm(
    t('negotiation.confirmAccept', { price: formatPrice(n.offeredPrice,n.currency), name: n.buyerName }),
    t('negotiation.acceptOfferTitle'),
    () => {
      store.acceptNegotiation(n.id)
      f7.toast.show({ text: t('negotiation.offerAccepted'), closeTimeout: 1500 })
    },
  )
}

function onReject(n) {
  f7.dialog.confirm(
    t('negotiation.confirmReject', { price: formatPrice(n.offeredPrice, n.currency), name: n.buyerName }),
    t('negotiation.rejectOfferTitle'),
    () => {
      store.rejectNegotiation(n.id, t('negotiation.offerRejectedReason'))
      f7.toast.show({ text: t('negotiation.offerRejected'), closeTimeout: 1500 })
    },
  )
}

function openCounterOffer(n) {
  counterTarget.value = n
  counterPrice.value = ''
  counterMessage.value = ''
  counterSheetOpen.value = true
}

function submitCounter() {
  if (!counterTarget.value || !counterPrice.value) return
  store.counterNegotiation(counterTarget.value.id, parseInt(counterPrice.value), counterMessage.value.trim())
  counterSheetOpen.value = false
  counterTarget.value = null
  f7.toast.show({ text: t('negotiation.counterOfferSent'), closeTimeout: 1500 })
}
</script>

<style scoped>
.neg-mgr-subtitle {
  font-size: 13px; color: var(--f7-on-surface-variant, #8e8e93); margin: 0;
}
.neg-mgr-tabs { margin: 8px 16px; }
.neg-tab-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
  background: var(--f7-theme-color); color: #fff; font-size: 10px; font-weight: 600;
  margin-left: 4px;
}
.neg-mgr-empty {
  text-align: center; padding: 40px 16px; color: var(--f7-on-surface-variant, #8e8e93);
}
.neg-mgr-empty p { margin-top: 8px; font-size: 14px; }
.neg-mgr-card { margin: 4px 16px; }
.neg-mgr-card-header { display: flex; justify-content: space-between; align-items: flex-start; }
.neg-mgr-buyer { display: flex; gap: 8px; align-items: center; flex: 1; min-width: 0; }
.neg-mgr-buyer-avatar {
  width: 32px; height: 32px; border-radius: 50%; background: var(--f7-theme-color);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-weight: 600; font-size: 14px; flex-shrink: 0;
}
.neg-mgr-buyer-avatar--accepted { background: #34c759; }
.neg-mgr-buyer-avatar--rejected { background: #ff3b30; }
.neg-mgr-buyer-avatar--countered { background: #007aff; }
.neg-mgr-buyer-name { font-size: 13px; font-weight: 600; }
.neg-mgr-product { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); }
.neg-mgr-offer-price { font-size: 16px; font-weight: 700; color: var(--f7-theme-color); white-space: nowrap; }
.neg-mgr-offer-price--accepted { color: #34c759; }
.neg-mgr-offer-price--rejected { color: #ff3b30; }
.neg-mgr-prices {
  display: flex; align-items: center; gap: 4px; white-space: nowrap;
}
.neg-mgr-price-original {
  font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); text-decoration: line-through;
}
.neg-mgr-price-counter { font-size: 16px; font-weight: 700; color: #007aff; }
.neg-mgr-message {
  font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93);
  font-style: italic; margin: 8px 0 0;
}
.neg-mgr-card-meta {
  display: flex; justify-content: space-between; margin-top: 8px;
  font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93);
}
.neg-mgr-accepted-meta { margin-top: 8px; font-size: 11px; color: #34c759; }
.neg-mgr-actions { display: flex; gap: 6px; margin-top: 10px; }
.neg-mgr-card--accepted, .neg-mgr-card--rejected, .neg-mgr-card--countered { opacity: 0.9; }
.neg-sheet-title { text-align: center; padding: 8px 16px 0; }
.neg-counter-hint { text-align: center; font-size: 13px; color: var(--f7-on-surface-variant, #8e8e93); padding: 0 16px; }
.neg-currency { font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); }
</style>
